import { useCallback, useEffect, useMemo, useState } from "react";
import type { Row, Solicitacao } from "../types";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { NovoDocumentoModal } from "./DocumentosView";
import { IconCheck, IconClose, IconRefresh } from "../icons";

interface Props {
  itens: Row[];
  user: string;
  displayName: string | null;
  onDataChanged: () => void;
}

const PRIORIDADE_PILL: Record<string, string> = {
  Urgente: "pill-danger",
  Alta: "pill-warn",
  Normal: "pill-neutral",
};

const STATUS_PILL: Record<string, string> = {
  Pendente: "pill-warn",
  Atendida: "pill-ok",
  Recusada: "pill-danger",
  Cancelada: "pill-neutral",
};

/** Peso de ordenação por prioridade (maior aparece primeiro). */
const PESO_PRIORIDADE: Record<string, number> = { Urgente: 3, Alta: 2, Normal: 1 };

function fmtData(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? s + "T00:00:00" : s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString("pt-BR");
}

/**
 * Aba "Solicitações" — a caixa de entrada dos pedidos de separação vindos da
 * Gestão da Operação (gravados na tabela `separacoes`). O almoxarife ATENDE
 * (gera o Termo de retirada, que baixa o estoque com foto + assinaturas) ou
 * RECUSA. O novo status volta a aparecer para quem pediu, no Operacional.
 */
export default function SolicitacoesView({ itens, user, displayName, onDataChanged }: Props) {
  const [rows, setRows] = useState<Solicitacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [soPendentes, setSoPendentes] = useState(true);
  const [atender, setAtender] = useState<Solicitacao | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows((await api.list("separacoes")) as unknown as Solicitacao[]);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const aposMudar = () => {
    load();
    onDataChanged(); // atualiza saldo/itens e o card de pendentes do Dashboard
  };

  const pendentes = useMemo(() => rows.filter((s) => s.status === "Pendente").length, [rows]);

  const lista = useMemo(() => {
    const arr = soPendentes ? rows.filter((s) => s.status === "Pendente") : rows;
    return [...arr].sort((a, b) => {
      // Pendentes primeiro; depois prioridade (Urgente→Normal); depois mais antigo.
      const pa = a.status === "Pendente" ? 0 : 1;
      const pb = b.status === "Pendente" ? 0 : 1;
      if (pa !== pb) return pa - pb;
      const wa = PESO_PRIORIDADE[a.prioridade ?? "Normal"] ?? 1;
      const wb = PESO_PRIORIDADE[b.prioridade ?? "Normal"] ?? 1;
      if (wa !== wb) return wb - wa;
      return String(a.data_solicitacao ?? "").localeCompare(String(b.data_solicitacao ?? ""));
    });
  }, [rows, soPendentes]);

  async function recusar(s: Solicitacao) {
    if (!confirm(`Recusar a solicitação de ${s.quantidade}× "${s.item_descricao ?? "item"}" de ${s.solicitante ?? "—"}?`))
      return;
    try {
      await api.update("separacoes", s.id, { status: "Recusada" });
      aposMudar();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erro ao recusar a solicitação.");
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="documentos">
      <div className="doc-head">
        <p className="muted">
          Pedidos de separação enviados pela Gestão da Operação. <strong>Atender</strong> gera o termo de
          retirada (foto + assinaturas) e baixa o estoque; o status volta para quem solicitou.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)} />
            Apenas pendentes
          </label>
          <span className="pill pill-warn">{pendentes} pendente(s)</span>
          <button className="btn-secondary btn-sm" onClick={load} title="Atualizar">
            <IconRefresh size={16} /> Atualizar
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando solicitações...</div>
        ) : lista.length === 0 ? (
          <div className="empty">
            {soPendentes
              ? "Nenhuma solicitação pendente. Os pedidos feitos na Gestão da Operação aparecem aqui."
              : "Nenhuma solicitação registrada."}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th style={{ textAlign: "right" }}>Qtd</th>
                <th>Prioridade</th>
                <th>Solicitante</th>
                <th>Pedido / justificativa</th>
                <th>Solicitado em</th>
                <th>Status</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="doc-item-nome">{s.item_descricao ?? `#${s.item_id}`}</div>
                    {s.item_codigo && <small className="muted">{s.item_codigo}</small>}
                  </td>
                  <td style={{ textAlign: "right" }}>{s.quantidade}</td>
                  <td>
                    <span className={`pill ${PRIORIDADE_PILL[s.prioridade ?? "Normal"] ?? "pill-neutral"}`}>
                      {s.prioridade ?? "Normal"}
                    </span>
                  </td>
                  <td>{s.solicitante ?? "—"}</td>
                  <td>{s.pedido ?? "—"}</td>
                  <td>{fmtData(s.data_solicitacao)}</td>
                  <td>
                    <span className={`pill ${STATUS_PILL[s.status] ?? "pill-neutral"}`}>{s.status}</span>
                  </td>
                  <td className="col-actions">
                    {s.status === "Pendente" ? (
                      <div className="doc-acoes">
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => setAtender(s)}
                          title="Atender: gerar o termo de retirada (baixa o estoque)"
                        >
                          <IconCheck size={15} /> Atender
                        </button>
                        <button
                          className="btn-secondary btn-sm btn-danger-text"
                          onClick={() => recusar(s)}
                          title="Recusar a solicitação"
                        >
                          <IconClose size={15} /> Recusar
                        </button>
                      </div>
                    ) : s.documento_id ? (
                      <small className="muted">Termo #{s.documento_id}</small>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {atender && (
        <NovoDocumentoModal
          itens={itens}
          responsavelPadrao={displayName || user}
          itemInicial={String(atender.item_id)}
          quantidadeInicial={String(atender.quantidade)}
          colaboradorInicial={atender.solicitante ?? ""}
          separacaoId={atender.id}
          titulo={`Atender solicitação #${atender.id} — termo de retirada`}
          textoBotao="Gerar termo e atender"
          onClose={() => setAtender(null)}
          onSaved={() => {
            setAtender(null);
            aposMudar();
          }}
        />
      )}
    </div>
  );
}
